import { useCallback } from 'react';
import { v4 as uuidv4 } from "uuid";
import { ProgramInputs } from "../schemas/inputs";
import useSaveProgram from './useSaveProgram';
import { availableEvaluationTypes } from "../support/availableEvaluationTypes";
import {EvaluationType} from "../schemas/types";

function getCurrentEvaluationType(originalEvaluationType: EvaluationType): EvaluationType{
    return availableEvaluationTypes.find(
        item => item.value === originalEvaluationType.value
    )?? availableEvaluationTypes[0]; 
}

export const useProgramForm = (originalChallenge?: any) => {
    const loadPayload = (data: ProgramInputs) => {
        return {
            data: {
                ...(originalChallenge?.data && {
                    createdAt: originalChallenge.data.createdAt,
                    updatedAt: originalChallenge.data.updatedAt,
                    isDeleted: originalChallenge.data.isDeleted,
                    owner: originalChallenge.data.owner,
                    status: originalChallenge.data.status,
                    timezone: originalChallenge.data.timezone,
                    language: originalChallenge.data.language
                }),
                isLaunched: originalChallenge?.data?.isLaunched ?? false,
                isActive: data.isActive == "public" ? false : true,
                isVisible: originalChallenge?.data?.isVisible ?? false,
                config: {
                    title: data.title,
                    ...(originalChallenge?.data?.config?.program && {
                        program: originalChallenge.data.config.program
                    }),
                    niches: data.niches?.map(niche => ({
                        id: niche.id,
                        name: niche.name,
                        ...(niche.isDeleted !== undefined && { isDeleted: niche.isDeleted }),
                        ...(niche.createdAt && { createdAt: niche.createdAt }),
                        ...(niche.updatedAt && { updatedAt: niche.updatedAt })
                    })) ?? [{
                        id: data.niches?.[0]?.id,
                        name: data.niches?.[0]?.name
                    }],
                    type: {
                        isPrivate: data.isActive == "public" ? true : false
                    },
                    restrictions: {
                        isInternal: false
                    },
                    isATemplate: originalChallenge?.data?.config?.isATemplate ?? false,
                    limitOfIdeas: data.limitOfIdeas,
                    files: []
                },
                style: {
                    shortDescription: data.shortDescription,
                    longDescription: data.longDescription,
                    ...(originalChallenge?.data?.style?.coverImagePath && {
                        coverImagePath: originalChallenge.data.style.coverImagePath
                    }),
                    youtubeLink: data.videos?.[0]?.url || originalChallenge?.data?.style?.youtubeLink || "",
                    youtubeLinkTitle: data.videos?.[0]?.name || originalChallenge?.data?.style?.youtubeLinkTitle || "",
                    youtubeLink2: data.videos?.[1]?.url || originalChallenge?.data?.style?.youtubeLink2 || "",
                    youtubeLink2Title: data.videos?.[1]?.name || originalChallenge?.data?.style?.youtubeLink2Title || "",
                    youtubeLink3: data.videos?.[2]?.url || originalChallenge?.data?.style?.youtubeLink3 || "",
                    youtubeLink3Title: data.videos?.[2]?.name || originalChallenge?.data?.style?.youtubeLink3Title || "",
                    isActive: data.isActive == "public" ? true : false,
                    sharingLink: originalChallenge?.data?.style?.sharingLink || ""
                },
                applicationForm: {
                    name: data.title,
                    launchDate: data.lauchDate,
                    applicationStartDate: data.lauchDate,
                    finalSelectionDate: data.finalSelectionDate,
                    closeStepsAutomatically: true,
                    sections: data.sections.map((section, sectionIndex) => {
                        const originalSection = originalChallenge?.data?.applicationForm?.sections?.[sectionIndex];
                        return {
                            fields: section.questions?.map((question, questionIndex) => {
                                const originalField = originalSection?.fields?.[questionIndex];
                                return {
                                    description: question.description,
                                    size: 1000,
                                    isRemovable: false,
                                    isRequired: question.isRequired,
                                    isDeleted: false,
                                    name: question.name,
                                    options: question.options?.map((option, optionIndex) => {
                                        return {
                                            value: option.value,
                                            order: optionIndex,
                                        }
                                    }),
                                    type: question.type.value,
                                    order: questionIndex,
                                    gradeInfo: originalField?.gradeInfo ?? null,
                                    ...(originalField?.id && { id: originalField.id }),
                                    ...(originalField?.createdAt && { createdAt: originalField.createdAt }),
                                    ...(originalField?.updatedAt && { updatedAt: originalField.updatedAt })
                                }
                            }),
                            isDeleted: false,
                            isHidden: false,
                            name: section.name,
                            order: sectionIndex,
                            isATemplate: originalSection?.isATemplate ?? false,
                            ...(originalSection?.id && { id: originalSection.id }),
                            ...(originalSection?.createdAt && { createdAt: originalSection.createdAt }),
                            ...(originalSection?.updatedAt && { updatedAt: originalSection.updatedAt })
                        }
                    }),
                    ...(originalChallenge?.data?.applicationForm?.id && { 
                        id: originalChallenge.data.applicationForm.id 
                    }),
                    ...(originalChallenge?.data?.applicationForm?.isDeleted !== undefined && { 
                        isDeleted: originalChallenge.data.applicationForm.isDeleted 
                    }),
                    ...(originalChallenge?.data?.applicationForm?.createdAt && { 
                        createdAt: originalChallenge.data.applicationForm.createdAt 
                    }),
                    ...(originalChallenge?.data?.applicationForm?.updatedAt && { 
                        updatedAt: originalChallenge.data.applicationForm.updatedAt 
                    })
                },
                evaluation: {
                    rounds: 1,
                    likertScale: 5,
                    steps: data.evaluations.map((evaluation, evaluationIndex) => {
                        const originalStep = originalChallenge?.data?.evaluation?.steps?.[evaluationIndex];
                        const currentEvaluationType = getCurrentEvaluationType(evaluation.type);
                        let step: any = {
                            isDeleted: false,
                            name: evaluation.name,
                            startDate: evaluation.startDate,
                            closingDate: evaluation.endDate,
                            typeId: currentEvaluationType.value,
                            weightPercent: currentEvaluationType.allowWeigth? evaluation.weightPercent:100,
                            order: evaluationIndex,
                            sections: [],
                            description: originalStep?.description ?? null,
                            ...(originalStep?.id && { id: originalStep.id }),
                            ...(originalStep?.createdAt && { createdAt: originalStep.createdAt }),
                            ...(originalStep?.updatedAt && { updatedAt: originalStep.updatedAt })
                        };
                        
                        if (originalStep?.type) {
                            step.type = originalStep.type;
                        }
                        
                        if(currentEvaluationType.allowMultSection) {
                            // Conformidade Especial
                            const originalSection = originalStep?.sections?.[0];
                            step.sections.push({
                                fields: evaluation.sections?.[0]?.criteria?.map((evaluationCriteria, index) => {
                                    const originalField = originalSection?.fields?.[index];
                                    return {
                                        name: evaluationCriteria.name,
                                        description: evaluationCriteria.description,
                                        type: originalField?.type || "boolean",
                                        options: originalField?.options || { min: 0, max: 1 },
                                        isRequired: originalField?.isRequired ?? false,
                                        weight: originalField?.weight ?? 1,
                                        order: index,
                                        gradeInfo: originalField?.gradeInfo ?? null,
                                        ...(originalField?.id && { id: originalField.id }),
                                        ...(originalField?.isDeleted !== undefined && { isDeleted: originalField.isDeleted }),
                                        ...(originalField?.createdAt && { createdAt: originalField.createdAt }),
                                        ...(originalField?.updatedAt && { updatedAt: originalField.updatedAt })
                                    }
                                }),
                                isDeleted: false,
                                name: originalSection?.name || 'Seção 1',
                                order: 0,
                                ...(originalSection?.id && { id: originalSection.id }),
                                ...(originalSection?.createdAt && { createdAt: originalSection.createdAt }),
                                ...(originalSection?.updatedAt && { updatedAt: originalSection.updatedAt })
                            });
                        } else {
                            // Outros tipos de avaliação
                            step.sections = evaluation.sections?.map((evaluationSection, evaluationSectionIndex) => {
                                const originalSection = originalStep?.sections?.[evaluationSectionIndex];
                                return {
                                    fields: evaluationSection.criteria?.map((criterion, criterionIndex) => {
                                        const originalField = originalSection?.fields?.[criterionIndex];
                                        return {
                                            ...criterion,
                                            ...(originalField?.id && { id: originalField.id }),
                                            ...(originalField?.type && { type: originalField.type }),
                                            ...(originalField?.options && { options: originalField.options }),
                                            ...(originalField?.isRequired !== undefined && { isRequired: originalField.isRequired }),
                                            ...(originalField?.weight !== undefined && { weight: originalField.weight }),
                                            ...(originalField?.order !== undefined && { order: originalField.order }),
                                            gradeInfo: originalField?.gradeInfo ?? null,
                                            ...(originalField?.isDeleted !== undefined && { isDeleted: originalField.isDeleted }),
                                            ...(originalField?.createdAt && { createdAt: originalField.createdAt }),
                                            ...(originalField?.updatedAt && { updatedAt: originalField.updatedAt })
                                        }
                                    }),
                                    isDeleted: false,
                                    name: 'Seção Padrão',
                                    order: evaluationSectionIndex,
                                    ...(originalSection?.id && { id: originalSection.id }),
                                    ...(originalSection?.createdAt && { createdAt: originalSection.createdAt }),
                                    ...(originalSection?.updatedAt && { updatedAt: originalSection.updatedAt })
                                }
                            });
                        }
                        return step;
                    }),
                    ...(originalChallenge?.data?.evaluation?.id && { 
                        id: originalChallenge.data.evaluation.id 
                    }),
                    ...(originalChallenge?.data?.evaluation?.isDeleted !== undefined && { 
                        isDeleted: originalChallenge.data.evaluation.isDeleted 
                    }),
                    ...(originalChallenge?.data?.evaluation?.createdAt && { 
                        createdAt: originalChallenge.data.evaluation.createdAt 
                    }),
                    ...(originalChallenge?.data?.evaluation?.updatedAt && { 
                        updatedAt: originalChallenge.data.evaluation.updatedAt 
                    })
                },
                timezone: originalChallenge?.data?.timezone || "America/Sao_Paulo",
                language: originalChallenge?.data?.language || "pt-BR"
            }
        };
    }

    const { save, isLoading: isSaving } = useSaveProgram(loadPayload)

    const onSubmit = useCallback(async (data: ProgramInputs, challengeId: string) => {
        try {
            let result = await save(data, challengeId);
            return result.data.id;
        } catch (error) {
            throw error
        }
    }, [save]);

    const onSubmitDraft = useCallback(async (data: ProgramInputs, challengeId: string) => {
        try {
            console.log(data);
            let result = await save(data, challengeId);
            return result.data.id;
        } catch (error) {
            throw error
        }
    }, [save]);

    return {
        onSubmit,
        onSubmitDraft,
        isSaving
    };
};
